/** Measured native recipes. Only bounded numbers and palette roles cross the trust boundary. */
export const BLOCK_VARIANTS = {
 questionnaire: ['cards','pills','list'], calculator: ['fields','result-card'],
 'entity-list': ['rows','cards','series-cards'], kpi: ['card','compact'],
 'progress-list': ['bar','ring'], steps: ['list','section-checklist'],
} as const;
export const COMPONENT_PARTS=['options','option','number','question','questionHeader','field','result','row','card','progress'] as const;
export type ComponentPart=typeof COMPONENT_PARTS[number];
export interface ComponentStyle {
 paddingBlockStart?:number;paddingBlockEnd?:number;paddingInlineStart?:number;paddingInlineEnd?:number;marginBlockEnd?:number;
 radius?:number; paddingBlock?:number; paddingInline?:number; borderWidth?:number; borderBlockEndWidth?:number; gap?:number;
 fontSize?:number; fontWeight?:number; lineHeight?:number; width?:number; height?:number;
 background?:string; borderColor?:string; color?:string;
}
export interface ComponentRecipe {variant?:string; parts?:Partial<Record<ComponentPart,ComponentStyle>>; selected?:ComponentStyle}
export type ComponentRecipes=Partial<Record<keyof typeof BLOCK_VARIANTS,ComponentRecipe>>;
const bounds:Record<string,[number,number]>={paddingBlockStart:[0,64],paddingBlockEnd:[0,64],paddingInlineStart:[0,64],paddingInlineEnd:[0,64],marginBlockEnd:[0,64],radius:[0,100],paddingBlock:[0,64],paddingInline:[0,64],borderWidth:[0,4],borderBlockEndWidth:[0,4],gap:[0,64],fontSize:[8,48],fontWeight:[100,900],lineHeight:[8,80],width:[8,96],height:[8,96]};
export const COMPONENT_RECIPE_VARIABLES=Object.keys(BLOCK_VARIANTS).flatMap(type=>[...COMPONENT_PARTS,'selected'].flatMap(part=>[...Object.keys(bounds),'background','borderColor','color'].map(key=>'--recipe-'+type+'-'+part+'-'+key)));
export function safeComponentStyle(raw:unknown):ComponentStyle {
 const out:Record<string,number|string>={}; if(!raw||typeof raw!=='object'||Array.isArray(raw))return out;
 for(const [key,value] of Object.entries(raw)){
  const limit=bounds[key];
  if(limit&&typeof value==='number'&&Number.isFinite(value)&&value>=limit[0]&&value<=limit[1])out[key]=value;
  if(['background','borderColor','color'].includes(key)&&typeof value==='string'&&/^(surface|surfaceRaised|ink|inkMuted|border|accent|accentSoft|onAccent)$/.test(value))out[key]=value;
 }
 return out;
}
export function safeComponentRecipes(raw:unknown):ComponentRecipes {
 const out:ComponentRecipes={}; if(!raw||typeof raw!=='object')return out;
 for(const type of Object.keys(BLOCK_VARIANTS) as (keyof typeof BLOCK_VARIANTS)[]){
  const r=(raw as Record<string,ComponentRecipe>)[type];if(!r||typeof r!=='object')continue;
  const recipe:ComponentRecipe={parts:{}};
  if((BLOCK_VARIANTS[type] as readonly string[]).includes(r.variant??''))recipe.variant=r.variant;
  for(const part of COMPONENT_PARTS)if(r.parts?.[part])recipe.parts![part]=safeComponentStyle(r.parts[part]);
  if(r.selected)recipe.selected=safeComponentStyle(r.selected);out[type]=recipe;
 }
 return out;
}
const palette:Record<string,string>={surface:'--module-surface',surfaceRaised:'--module-surface-raised',ink:'--module-ink',inkMuted:'--module-ink-muted',border:'--module-border',accent:'--module-accent',accentSoft:'--module-accent-soft',onAccent:'--module-on-accent'};
export function componentRecipeVariables(raw:unknown):Record<string,string>{
 const out:Record<string,string>={};
 for(const [type,recipe] of Object.entries(safeComponentRecipes(raw))){
  for(const [part,style] of Object.entries({...recipe.parts,selected:recipe.selected})){
   for(const [key,value] of Object.entries(style??{}))out['--recipe-'+type+'-'+part+'-'+key]=typeof value==='number'?String(value)+(key==='fontWeight'?'':'px'):'var('+palette[value]+')';
  }
 }
 return out;
}
